import React, { useState } from 'react';
import { css } from '@emotion/react';

import { gray2, gray4, primary } from 'styles/global_defaults/colors';
import { halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';

import ColorPickerPopover, { ColorPickerPopoverProps, ColorValuesMap } from 'components/color-picker-popover';
import { Placement } from 'react-bootstrap/esm/Overlay';


type Props<ColorValuesMapT extends ColorValuesMap> =
  Pick<ColorPickerPopoverProps<ColorValuesMapT>, 'onChange' | 'colorValues' | 'sections' | 'show' | 'onToggle' | 'container'> & {
    placeholder?: string;
    placement?: Placement;
    showCircle?: boolean;
  };


export const NvColorPickerWithInput = <ColorValuesMapT extends ColorValuesMap>(props: Props<ColorValuesMapT>) => {
  const { onChange, colorValues, sections, placeholder, placement, showCircle = true, show: propsShow, onToggle: controlledOnToggle, container } = props;

  const styles = css`
    .color-input-wrapper {
      position: relative;
      width: 160px;
    }

    #color-input {
      padding-right: 35px !important;
    }

    input {
      width: 100%;
      border: 1px solid ${gray4};

      &:focus-visible {
        outline: none !important;
        border: 1px solid ${primary} !important;
      }

      ::placeholder {
        color: ${gray2};
      }
    }

    .circle {
      height: ${standardSpacing}px;
      width: ${standardSpacing}px;
      border-radius: 100%;
      border: 1px solid ${gray4};
      position: absolute;
      top: ${halfSpacing}px;
      right: ${halfSpacing}px;
      background-color: ${colorValues[sections[0].name]};
    }
  `;

  const [internalShowPicker, setInternalShowPicker] = useState(propsShow ?? false);
  const isControlled = propsShow !== undefined;
  const show = isControlled ? propsShow : internalShowPicker;

  const onToggle = (nextShow: boolean) => {
    if (!isControlled) {
      setInternalShowPicker(nextShow);
    }
    controlledOnToggle?.(nextShow);
  };

  return (
    <div css={styles}>
      <ColorPickerPopover
        show={show}
        onToggle={onToggle}
        onChange={onChange}
        colorValues={colorValues}
        placement={placement}
        sections={sections}
        container={container}
      >
        <div className='color-input-wrapper'>
          <input
            id='color-input'
            className='text-medium p-2'
            value={colorValues[sections[0].name] || ''}
            placeholder={placeholder}
            onChange={(e) => onChange(sections[0].name, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggle(!show);
              }
              if (e.key === 'Escape' && show) {
                e.preventDefault();
                onToggle(false);
              }
            }}
          />
          {showCircle && <div className='circle' />}
        </div>
      </ColorPickerPopover>
    </div>
  );
};

export default NvColorPickerWithInput;
